import { useEffect, useRef, useState } from 'react'
import { Pin } from 'lucide-react'

import { cn } from '@modules/lib/lib'
import { Button } from '@modules/shadcn/ui/Button/Button'
import { CheckBox } from '@modules/shadcn/ui/CheckBox/CheckBox'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@modules/shadcn/ui/Dialog/Dialog'
import { Input } from '@modules/shadcn/ui/Input/Input'

import {
  AI_COLOR_PIN_LIMIT,
  countHistoryTitleLength,
  HISTORY_TITLE_MAX_LENGTH,
  type ResolvedHistoryTitle,
} from '../utils/historyDisplay'
import { isImeComposingKeyEvent } from '../utils/isImeComposingKeyEvent'

/** 共有リンクの有効期限日数（バックエンドと同期） */
const SHARE_LINK_EXPIRATION_DAYS = 90

/** 共有ダイアログに出す「共有する履歴」のプレビュー（issue #2950） */
export interface SharedHistoryPreview {
  url?: string
  title: ResolvedHistoryTitle
  /** サーバーに保存されているタイトル（未設定は空文字列）。ダイアログの入力欄の初期値 */
  savedTitle: string
  cutLabel: string
  createdBy?: string
  createdAt?: string
  isPinned: boolean
  favorited: boolean
}

/**
 * 履歴に紐づけずプロジェクト単位のリンクにする理由（決定事項4。design.md D5）。
 * - unexecutedChanges: 色を変更したあと実行していない
 * - noHistorySelected: 現況画像を選択していて履歴を選んでいない
 */
export type ShareFallbackReason = 'unexecutedChanges' | 'noHistorySelected'

/** 「この履歴をピン留めする」チェックボックスの状態（design.md D4） */
export interface SharePinState {
  count: number
  isPinned: boolean
  pending: boolean
  onToggle: (pinned: boolean) => Promise<void> | void
}

interface ShareDialogProps {
  open: boolean
  projectName: string
  shareUrl?: string
  isLoading?: boolean
  errorMessage?: string
  onOpenChange: (open: boolean) => void
  onCopy?: () => Promise<void> | void
  /** 共有する履歴（アンカー）。無ければプロジェクト単位のリンク */
  sharedHistory?: SharedHistoryPreview
  /** プロジェクト単位にした理由。sharedHistoryが無いときに出す */
  fallbackReason?: ShareFallbackReason
  /** 共有する履歴を選び直す（ダイアログを閉じる） */
  onPickAnotherHistory?: () => void
  /** 「閉じて実行する」（ダイアログを閉じて実行ボタンへ導く） */
  onCloseAndExecute?: () => void
  pinState?: SharePinState
  /**
   * 共有する履歴のタイトルをその場で保存する（藤井の決定、2026-10-28）。
   * 空文字列は未設定に戻す。失敗は例外で返す（ダイアログが入力欄の下に出す）。無ければ入力欄を出さない
   */
  onSaveTitle?: (title: string) => Promise<void> | void
  /** リンクの有効期限（サーバーが返す値） */
  expiresAt?: Date
  /** 選択中の履歴のタイトル（フォールバックの案内文で名指しするため） */
  selectedHistoryTitle?: string
}

interface ShareTitleFieldProps {
  savedTitle: string
  onSave: (title: string) => Promise<void> | void
}

/**
 * 共有ダイアログの中でタイトルを付ける入力欄。
 * 「この履歴を見て」と共有する場面がタイトルを付ける動機そのものなので、履歴カードに戻らずその場で付けられるようにする。
 * Enterで保存・フォーカスを外しても保存（企業画面の履歴カードと同じ）。保存したタイトルは帯と戻りピルに出る。
 */
const ShareTitleField: React.FC<ShareTitleFieldProps> = ({ savedTitle, onSave }) => {
  const [value, setValue] = useState(savedTitle)
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved'>('idle')
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    setValue(savedTitle)
    setError(null)
  }, [savedTitle])

  useEffect(
    () => () => {
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current)
    },
    [],
  )

  const length = countHistoryTitleLength(value)
  const tooLong = length > HISTORY_TITLE_MAX_LENGTH

  const commit = async () => {
    const next = value.trim()
    if (tooLong || next === savedTitle.trim()) return
    setStatus('saving')
    setError(null)
    try {
      await onSave(next)
      setStatus('saved')
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current)
      savedTimerRef.current = setTimeout(() => setStatus('idle'), 2000)
    } catch (err) {
      console.error('[ShareDialog] タイトルの保存に失敗:', err)
      setStatus('idle')
      setError('タイトルを保存できませんでした。もう一度お試しください。')
    }
  }

  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs text-muted-foreground" htmlFor="share-dialog-title">
        タイトル
      </label>
      <Input
        aria-describedby="share-dialog-title-help"
        aria-invalid={tooLong}
        className="h-8 bg-background text-sm"
        id="share-dialog-title"
        onBlur={() => void commit()}
        onChange={event => setValue(event.target.value)}
        onKeyDown={event => {
          // 日本語変換を確定するEnterでは保存しない（確定後にもう一度Enterで保存）
          if (isImeComposingKeyEvent(event)) return
          if (event.key === 'Enter') {
            event.preventDefault()
            inputRef.current?.blur()
          }
        }}
        placeholder="例：A案 ブラウン×チャコール"
        ref={inputRef}
        value={value}
      />
      <p
        className={cn('text-[11px] text-muted-foreground', tooLong && 'text-destructive')}
        id="share-dialog-title-help"
      >
        {status === 'saving'
          ? '保存中...'
          : status === 'saved'
            ? '保存しました。'
            : 'お施主様の画面にも表示されます。Enterで保存。'}{' '}
        {length}/{HISTORY_TITLE_MAX_LENGTH}
      </p>
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  )
}

const formatExpiry = (expiresAt: Date | undefined): string => {
  const date = expiresAt ?? new Date(Date.now() + SHARE_LINK_EXPIRATION_DAYS * 24 * 60 * 60 * 1000)
  return `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`
}

/**
 * 共有ダイアログ（issue #2950）。
 *
 * - 履歴を指すリンク: 「共有する履歴」のプレビュー（タイトルはその場で付けられる）、「別の履歴を選ぶ」、ピン留めのチェックボックス
 * - プロジェクト単位のリンク（未実行の変更あり等）: 「共有する範囲」の案内と「閉じて実行する」。
 *   ピン留めのチェックボックスは出さない（決定事項4）
 */
export const ShareDialog: React.FC<ShareDialogProps> = ({
  open,
  projectName,
  shareUrl,
  isLoading = false,
  errorMessage,
  onOpenChange,
  onCopy,
  sharedHistory,
  fallbackReason,
  onPickAnotherHistory,
  onCloseAndExecute,
  pinState,
  onSaveTitle,
  expiresAt,
  selectedHistoryTitle,
}) => {
  const [copied, setCopied] = useState(false)
  const [copyError, setCopyError] = useState<string | null>(null)
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleCopy = async () => {
    if (!shareUrl) return
    setCopyError(null)
    try {
      if (onCopy) {
        await onCopy()
      } else if (navigator?.clipboard) {
        await navigator.clipboard.writeText(shareUrl)
      } else {
        throw new Error('clipboard unavailable')
      }
    } catch {
      setCopyError('コピーに失敗しました。もう一度お試しください。')
      return
    }
    setCopied(true)
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current)
    copyTimerRef.current = setTimeout(() => setCopied(false), 2000)
  }

  const isAnchored = Boolean(sharedHistory)
  const pinLimitReached =
    pinState !== undefined && !pinState.isPinned && pinState.count >= AI_COLOR_PIN_LIMIT

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="max-w-[640px]">
        <DialogHeader>
          <DialogTitle>{projectName}を共有</DialogTitle>
        </DialogHeader>
        <div className="space-y-6">
          {sharedHistory ? (
            <section className="space-y-2">
              <p className="text-sm font-medium text-foreground">共有する履歴</p>
              <div className="flex flex-wrap items-center gap-3 rounded-lg border border-input bg-muted/40 p-3">
                <div className="h-[72px] w-[96px] shrink-0 overflow-hidden rounded-md border border-input bg-muted">
                  {sharedHistory.url ? (
                    <img alt="" className="h-full w-full object-cover" src={sharedHistory.url} />
                  ) : null}
                </div>
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  {onSaveTitle ? (
                    <ShareTitleField onSave={onSaveTitle} savedTitle={sharedHistory.savedTitle} />
                  ) : (
                    <p
                      className={cn(
                        'truncate text-sm',
                        sharedHistory.title.isDefault
                          ? 'text-muted-foreground'
                          : 'font-semibold text-foreground',
                      )}
                    >
                      {sharedHistory.title.text}
                    </p>
                  )}
                  <p className="truncate text-xs text-muted-foreground">
                    {[sharedHistory.cutLabel, sharedHistory.createdBy, sharedHistory.createdAt]
                      .filter(Boolean)
                      .join('・')}
                  </p>
                  {sharedHistory.isPinned || sharedHistory.favorited ? (
                    <div className="flex gap-1.5">
                      {sharedHistory.isPinned ? (
                        <span className="inline-flex items-center gap-1 rounded-full border border-orange-300 bg-orange-50 px-2 text-[10px] leading-4 text-orange-800">
                          <Pin aria-hidden className="h-2.5 w-2.5 fill-current" />
                          ピン留め中
                        </span>
                      ) : null}
                      {sharedHistory.favorited ? (
                        <span className="inline-flex items-center rounded-full border border-emerald-300 bg-emerald-50 px-2 text-[10px] leading-4 text-emerald-800">
                          お気に入り
                        </span>
                      ) : null}
                    </div>
                  ) : null}
                </div>
                {onPickAnotherHistory ? (
                  <Button onClick={onPickAnotherHistory} size="sm" type="button" variant="outline">
                    別の履歴を選ぶ
                  </Button>
                ) : null}
              </div>
              <p className="text-xs text-muted-foreground">
                お施主様がリンクを開くと、この履歴が表示されます。
              </p>
            </section>
          ) : (
            <section className="space-y-2">
              <p className="text-sm font-medium text-foreground">共有する範囲</p>
              <div className="flex flex-wrap items-start gap-3 rounded-lg border border-input bg-muted/40 p-3">
                <div className="flex h-[72px] w-[96px] shrink-0 items-center justify-center rounded-md border border-dashed border-input bg-muted text-center text-[11px] font-semibold text-muted-foreground">
                  {fallbackReason === 'noHistorySelected' ? (
                    <>
                      履歴の
                      <br />
                      指定なし
                    </>
                  ) : (
                    <>
                      未実行の
                      <br />
                      変更あり
                    </>
                  )}
                </div>
                <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                  <p className="text-sm font-semibold text-foreground">
                    プロジェクト全体を共有します（履歴の指定なし）
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {fallbackReason === 'noHistorySelected'
                      ? '履歴を選択していないため、このリンクは特定の履歴に紐づけません。お施主様がリンクを開くと、最新の履歴が表示されます。'
                      : '色を変更したあと実行していないため、このリンクは特定の履歴に紐づけません。お施主様がリンクを開くと、最新の履歴が表示されます。'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {fallbackReason === 'noHistorySelected'
                      ? '特定の履歴に着地させたい場合は、履歴を選んでから共有してください。'
                      : `いま選択している${selectedHistoryTitle ? `「${selectedHistoryTitle}」` : '履歴'}や、変更中の色に着地させたい場合は、先に「実行」して履歴にしてから共有してください。`}
                  </p>
                </div>
              </div>
              {onCloseAndExecute && fallbackReason !== 'noHistorySelected' ? (
                <div className="flex justify-end">
                  <Button onClick={onCloseAndExecute} size="sm" type="button" variant="outline">
                    閉じて実行する
                  </Button>
                </div>
              ) : null}
            </section>
          )}

          {isAnchored && pinState ? (
            <div className="space-y-1">
              <label
                className={cn(
                  'flex items-center gap-2 text-sm',
                  pinLimitReached ? 'text-muted-foreground' : 'cursor-pointer text-foreground',
                )}
                htmlFor="share-dialog-pin"
              >
                <CheckBox
                  checked={pinState.isPinned}
                  disabled={pinState.pending || pinLimitReached}
                  id="share-dialog-pin"
                  onCheckedChange={checked => void pinState.onToggle(Boolean(checked))}
                />
                <span>この履歴をピン留めする</span>
              </label>
              {/* 件数は「あとN件」の文で出す。「いまN/4」は読み手に意味が伝わらない（藤井の指摘） */}
              <p className="pl-6 text-xs text-muted-foreground">
                {pinLimitReached
                  ? `ピン留めは${AI_COLOR_PIN_LIMIT}件までです。入れ替えるには、履歴一覧のピンのボタンから外してください。`
                  : pinState.isPinned
                    ? 'ピン留めした履歴は、施主画面の先頭に並びます。'
                    : `ピン留めした履歴は、施主画面の先頭に並びます。あと${AI_COLOR_PIN_LIMIT - pinState.count}件ピン留めできます。`}
              </p>
            </div>
          ) : null}

          <section className="space-y-2">
            <p className="text-sm font-medium text-foreground">共有リンク</p>
            <div className="flex items-center gap-2 rounded-md bg-[#F4F4F5] p-3">
              <Input
                aria-label="共有リンク"
                className="border-none bg-transparent text-sm"
                readOnly
                value={isLoading ? 'リンクを生成中...' : (shareUrl ?? '')}
              />
              <Button
                disabled={!shareUrl || isLoading}
                onClick={handleCopy}
                size="sm"
                type="button"
                variant="outline"
              >
                {copied ? 'コピー済み' : 'コピー'}
              </Button>
            </div>
            {errorMessage || copyError ? (
              <p className="text-xs text-red-600">{errorMessage ?? copyError}</p>
            ) : null}
            {shareUrl && !isLoading ? (
              <p className="text-xs text-muted-foreground">
                有効期限：{formatExpiry(expiresAt)}まで（発行から{SHARE_LINK_EXPIRATION_DAYS}日）
              </p>
            ) : null}
          </section>

          <section className="space-y-2">
            <p className="text-sm font-medium text-foreground">共有された方ができること</p>
            <ul className="list-disc space-y-1 pl-4 text-sm text-slate-700">
              <li>
                {isAnchored ? 'この履歴を見た状態から' : '最新の履歴を見た状態から'}
                、色を変えてシミュレーションを試す
              </li>
              <li>プロジェクト内のほかの履歴やピン留めした履歴を見る</li>
              <li>履歴にタイトルを付ける、お気に入りにする</li>
            </ul>
          </section>

          <section className="space-y-2">
            <p className="text-sm font-medium text-foreground">共有時の注意事項</p>
            <ul className="list-disc space-y-1 pl-4 text-sm text-slate-700">
              <li>共有相手が生成した画像はプロジェクト内に保存されます</li>
              <li>共有リンクへのアクセス可能な有効期限は{SHARE_LINK_EXPIRATION_DAYS}日です</li>
            </ul>
          </section>
        </div>
        <DialogFooter className="sm:justify-end">
          <Button onClick={() => onOpenChange(false)} type="button">
            閉じる
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
